import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { useAlertWebSocket } from "../../contexts/WebSocketContext";
import { useActiveAlerts } from "../../contexts/ActiveAlertsContext";
import { fetchAlertsList, fetchSensorAlerts } from "../../services/alertService";
import { getItem, logout } from "../../utils/authStorage";
import { relativeTimeFrom } from "../../utils/helpers";
import { parseJwt } from "../../utils/jwtDecoder";
import CustomSelect from "../shared/CustomSelect";
import Header from "../shared/Header";
import {
  ShieldIcon,
  TablerCalender,
  TablerHigh,
  TablerLow,
  TablerMedium,
  TablerSearch
} from "../shared/Icons";
import LoadingSpinner from "../shared/LoadingSpinner";
import { ALERT_STATUS_OPTIONS, ROWS_PER_PAGE_OPTIONS, SEVERITY_OPTIONS } from "../shared/selectOptions";
import Sidebar from "../shared/Sidebar";
import ActiveAlertCard from "./ActiveAlertCard";
import AlertDetailPage from "./AlertDetailPage";
import { parseDateInput, formatDisplayDate } from "../../utils/dateUtils";
import "./AlertsPage.css";

function AlertsPage() {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  
  const [activeTab, setActiveTab] = useState("active"); // Default to Active Alerts as per Figma screen flow
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const selectedAlertId = searchParams.get("alertId");
  const selectedAlertType = searchParams.get("type");

  const { activeAlerts } = useActiveAlerts();

  // Alert data state
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter state
  const [filterId, setFilterId] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [dateInputFocused, setDateInputFocused] = useState(false);
  const [filterSeverity, setFilterSeverity] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Expanded alert for device view in table
  const [expandedAlertId, setExpandedAlertId] = useState(null);



  // WebSockets context
  const { isConnected: wsConnected, subscribe } = useAlertWebSocket();

  // Load alerts
  const loadAlerts = async () => {
    try {
      const [cameraData, sensorData] = await Promise.all([
        fetchAlertsList(),
        fetchSensorAlerts(),
      ]);
      const normalizedSensorAlerts = (sensorData || []).map((sensor) => ({
        id: sensor.sensor_alert,
        type: "sensor",
        sensor_id: sensor.sensor_id,
        drone_id: sensor.drone_id,
        drone_flight_id: sensor.drone_flight_id,
        message: sensor.description,
        start_time: sensor.timestamp,
        alert_analysis: sensor.status,
        title: sensor.description || "Sensor Alert",
        severity: sensor.severity,
        is_active: sensor.status === "Active" || sensor.is_active,
        drone: sensor?.drone,
        devices: sensor?.devices || [],
      }));
      const combined = [...(cameraData || []), ...normalizedSensorAlerts];
      setAlerts(combined);
    } catch (error) {
      console.error("Error loading alerts:", error);
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAlerts();

    let debounceTimer;
    const scheduleRefresh = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        loadAlerts();
      }, 500);
    };

    const unsub = subscribe((raw) => {
      const type = raw?.type;
      if (
        type === "alert.create" ||
        type === "alert.complete" ||
        type === "drone.alert" ||
        type === "drone.complete" ||
        type === "alert.verify" ||
        type === "alert.update"
      ) {
        scheduleRefresh();
      }
    });
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      unsub();
    };
  }, [subscribe]);

  const isInitialMount = React.useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (!selectedAlertId) {
      loadAlerts();
    }
  }, [selectedAlertId]);


  const token = getItem("accessToken");
  const tokenPayload = token ? parseJwt(token) : null;
  const username =
    getItem("username") ||
    tokenPayload?.username ||
    tokenPayload?.preferred_username ||
    "User";
  const rawRole =
    getItem("role") || tokenPayload?.role || tokenPayload?.roles?.[0] || "Operator";
  const role = rawRole.toLowerCase();

  const handleLogout = () => {
    logout();
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "N/A";
    const date = new Date(isoString);
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    let hh = date.getHours();
    const min = String(date.getMinutes()).padStart(2, "0");
    const ampm = hh >= 12 ? "PM" : "AM";
    hh = hh % 12 || 12;
    return `${dd}-${mm}-${yyyy} ${String(hh).padStart(2, "0")}:${min} ${ampm}`;
  };

  // Truncate alert ID for display
  const truncateId = (id) => {
    if (!id) return "Unknown ID";
    if (id.length <= 16) return id;
    return `${id.substring(0, 8)}…${id.substring(id.length - 6)}`;
  };

  // Dynamic status text calculations
  const lastProcessedText = useMemo(() => {
    if (alerts.length === 0) return "N/A";
    const sorted = [...alerts].sort((a, b) => new Date(b.start_time || 0) - new Date(a.start_time || 0));
    const latest = sorted[0];
    if (!latest.start_time) return "N/A";
    return relativeTimeFrom(latest.start_time);
  }, [alerts]);

  // Filtered alerts
  const filteredAlerts = useMemo(() => {
    const result = alerts.filter((alert) => {
      const matchesId = !filterId || (alert.id && alert.id.includes(filterId));
      const matchesSeverity =
        filterSeverity === "All" ||
        (alert.severity &&
          alert.severity.toLowerCase() === filterSeverity.toLowerCase());
      const matchesStatus =
        filterStatus === "All" ||
        (alert.alert_analysis &&
          alert.alert_analysis.toLowerCase() === filterStatus.toLowerCase());
      const matchesDate =
        !filterDate ||
        (() => {
          const selectedDate = parseDateInput(filterDate);
          if (!selectedDate || !alert.start_time) return false;
          const alertDate = new Date(alert.start_time);
          return (
            alertDate.getDate() === selectedDate.getDate() &&
            alertDate.getMonth() === selectedDate.getMonth() &&
            alertDate.getFullYear() === selectedDate.getFullYear()
          );
        })();
      return matchesId && matchesSeverity && matchesStatus && matchesDate;
    });
    return result.sort(
      (a, b) => new Date(b.start_time || 0) - new Date(a.start_time || 0)
    );
  }, [alerts, filterId, filterSeverity, filterStatus, filterDate]);

  // Paginated alerts
  const totalPages = Math.max(1, Math.ceil(filteredAlerts.length / rowsPerPage));
  const pagedAlerts = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredAlerts.slice(start, start + rowsPerPage);
  }, [filteredAlerts, currentPage, rowsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(totalPages, page)));
  };

  const handleReset = () => {
    setFilterId("");
    setFilterDate("");
    setDateInputFocused(false);
    setFilterSeverity("All");
    setFilterStatus("All");
    setCurrentPage(1);
  };



  // Severity badge class
  const getSeverityClass = (severity) => {
    if (!severity) return "badge-severity-low";
    const s = severity.toLowerCase();
    if (s === "high") return "badge-severity-high";
    if (s === "medium") return "badge-severity-medium";
    return "badge-severity-low";
  };

  const getSeverityIcon = (severity) => {
    if (!severity) return null;
    const s = severity.toLowerCase();
    if (s === "high") return <TablerHigh size={14} />;
    if (s === "medium") return <TablerMedium size={14} />;
    if (s === "low") return <TablerLow size={14} />;
    return null;
  };

  const formatBadgeLabel = (value) => {
    if (!value) return "N/A";
    return String(value)
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // Status badge class
  const getStatusClass = (status) => {
    if (!status) return "badge-status-default";
    const s = status.toLowerCase();
    if (s === "active") return "badge-status-active";
    if (s === "processed") return "badge-status-processed";
    if (s === "completed" || s === "complete") return "badge-status-completed";
    return "badge-status-default";
  };

  const visibleAlerts =
    activeTab === "all" ? pagedAlerts : activeAlerts;

  if (selectedAlertId) {
    return (
      <AlertDetailPage
        alert={{ id: selectedAlertId, type: selectedAlertType }}
        onClose={() => {
          if (window.history && window.history.length > 1) {
            navigate(-1);
          } else {
            setSearchParams({});
          }
        }}
      />
    );
  }

  return (
    <div className={`alerts-page ${isDarkMode ? "dark" : "light"}`}>
      <Sidebar
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed(prev => !prev)}
        isDarkMode={isDarkMode}
        username={username}
        role={role}
        onLogout={handleLogout}
      />

      {/* Main Content */}
      <main className="alerts-main-content">
        {/* Top Navigation Bar */}
        <Header title={t("nav.alerts")} />

        {/* Main divider */}
        <div className="alerts-main-divider" />

        {/* Alerts Content */}
        <div className="alerts-content">
          {/* Tab Bar */}
          <div className="alerts-tab-bar">
            <button
              className={`alerts-tab-pill ${
                activeTab === "active" ? "active" : ""
              }`}
              onClick={() => setActiveTab("active")}
            >
              {t("label.activeAlerts", { count: activeAlerts.length })}
            </button>
            <button
              className={`alerts-tab-pill ${
                activeTab === "all" ? "active" : ""
              }`}
              onClick={() => setActiveTab("all")}
            >
              {t("label.allAlerts", { count: alerts.length })}
            </button>
          </div>

          {/* WebSocket Status & Subheader Bar */}
          {activeTab === "active" && (
            <div className="alerts-websocket-banner">
              <div className="ws-status">
                WebSocket:{" "}
                <span className={`ws-status-val ${wsConnected ? "connected" : "disconnected"}`}>
                  {wsConnected ? "connected" : "disconnected"}
                </span>
              </div>
              <div className="last-processed">
                Last alert processed: {lastProcessedText}
              </div>
            </div>
          )}

          {activeTab === "all" && (
            /* Filter Row */
            <div className="alerts-filter-row">
              <div className="alerts-filter-inputs">
                {/* Search by Alert ID */}
                <div className="alerts-filter-field">
                  <div className="alerts-input-wrapper">
                    <TablerSearch className="alerts-input-icon" size={14} color="currentColor" />
                    <input
                      type="text"
                      placeholder={t("placeholder.searchByAlertId")}
                      value={filterId}
                      onChange={(e) => setFilterId(e.target.value)}
                      className="alerts-filter-input"
                    />
                  </div>
                </div>

                {/* Date Filter */}
                <div className="alerts-filter-field">
                  <div
                    className={`alerts-input-wrapper alerts-date-wrapper ${dateInputFocused ? "alerts-date-focused" : ""}`}
                    style={{ cursor: "pointer" }}
                    onClick={(e) => {
                      const dateInput = e.currentTarget.querySelector(".alerts-date-input");
                      if (dateInput && typeof dateInput.showPicker === "function") {
                        try {
                          dateInput.showPicker();
                        } catch (err) {
                          console.warn("Failed to open date picker using showPicker()", err);
                        }
                      }
                    }}
                  >
                    <input
                      type="date"
                      value={filterDate}
                      onFocus={() => setDateInputFocused(true)}
                      onBlur={() => setDateInputFocused(false)}
                      onChange={(e) => setFilterDate(e.target.value)}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (typeof e.target.showPicker === "function") {
                          e.preventDefault();
                          try {
                            e.target.showPicker();
                          } catch (err) {
                            console.warn("Failed to open date picker using showPicker()", err);
                          }
                        }
                      }}
                      className="alerts-filter-input alerts-date-input"
                      aria-label="Filter by date in DD/MM/YYYY format"
                    />
                    <span className={`alerts-date-display ${filterDate ? "has-value" : ""}`}>
                      {filterDate ? formatDisplayDate(filterDate) : "DD/MM/YYYY"}
                    </span>
                    <span className="alerts-date-icon">
                      <TablerCalender size={15} color="currentColor" />
                    </span>
                  </div>
                </div>

                {/* Severity Filter */}
                <div className="alerts-filter-field">
                  <CustomSelect
                    value={filterSeverity}
                    onChange={setFilterSeverity}
                    options={SEVERITY_OPTIONS.map(o => ({ ...o, label: t(o.labelKey) }))}
                  />
                </div>

                {/* Status Filter */}
                <div className="alerts-filter-field">
                  <CustomSelect
                    value={filterStatus}
                    onChange={setFilterStatus}
                    options={ALERT_STATUS_OPTIONS.map(o => ({ ...o, label: t(o.labelKey) }))}
                  />
                </div>
              </div>

              {/* Reset Button */}
              <button className="alerts-reset-btn" onClick={handleReset}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                  <path d="M3 3v5h5" />
                </svg>
                {t("button.reset")}
              </button>
            </div>
          )}

          {/* Alerts Content Area */}
          <div className={activeTab === "active" ? "active-alerts-container-free" : "alerts-table-container"}>
            {loading ? (
              <LoadingSpinner message={t("loading.alerts")} />
            ) : activeTab === "active" ? (
              activeAlerts.length === 0 ? (
                <div className="alerts-no-records">
                  <div className="alerts-no-records-icon">
                    <ShieldIcon color="#00AF64" size={28} />
                  </div>
                  <h2>{t("label.noActiveThreats")}</h2>
                  <p>
                    {t("label.noActiveThreatsSubtitle")}
                  </p>
                </div>
              ) : (
                <div className="active-alerts-card-list">
                  {activeAlerts.map((alert, index) => (
                    <ActiveAlertCard
                      key={alert.id || index}
                      alert={alert}
                      formatDateTime={formatDateTime}
                      setSearchParams={setSearchParams}
                    />
                  ))}
                </div>
              )
            ) : pagedAlerts.length === 0 ? (
              <div className="alerts-no-records">
                <h2>{t("label.noDataFound")}</h2>
              </div>
            ) : (
              <div className="alerts-grid-table">
                <div className="alerts-grid-header">
                  <div className="alerts-grid-cell alerts-grid-cell-first">{t("label.alertId")}</div>
                  <div className="alerts-grid-cell">{t("label.timestampCol")}</div>
                  <div className="alerts-grid-cell">{t("label.severity")}</div>
                  <div className="alerts-grid-cell">{t("label.message")}</div>
                  <div className="alerts-grid-cell">{t("label.statusLabel")}</div>
                  <div className="alerts-grid-cell alerts-grid-cell-last">{t("label.action")}</div>
                </div>

                <div className="alerts-grid-body">
                  {visibleAlerts.map((alert, index) => (
                    <React.Fragment key={alert.id || index}>
                      <div className="alerts-grid-row">
                        <div className="alerts-grid-cell alerts-grid-cell-first">
                          <Link
                            className="alerts-link-button"
                            to={`/alert-details?alertId=${encodeURIComponent(
                              alert.id
                            )}&type=${alert.type || "camera"}`}
                            onClick={(e) => {
                              e.preventDefault();
                              setSearchParams({ alertId: alert.id, type: alert.type || "camera" });
                            }}
                          >
                            {truncateId(alert.id)}
                          </Link>
                        </div>
                        <div className="alerts-grid-cell">{formatDateTime(alert.start_time)}</div>
                        <div className="alerts-grid-cell">
                          <span
                            className={`alerts-badge ${getSeverityClass(
                              alert.severity
                            )}`}
                          >
                            {getSeverityIcon(alert.severity)}
                            {formatBadgeLabel(alert.severity)}
                          </span>
                        </div>
                        <div className="alerts-grid-cell alerts-description-cell">
                          <span className="alerts-description-text">
                            {alert.message || "No description"}
                          </span>
                        </div>
                        <div className="alerts-grid-cell">
                          <span
                            className={`alerts-badge ${getStatusClass(
                              alert.alert_analysis
                            )}`}
                          >
                            {formatBadgeLabel(alert.alert_analysis)}
                          </span>
                        </div>
                        <div className="alerts-grid-cell alerts-grid-cell-last">
                          <button
                            className="alerts-view-devices-btn"
                            onClick={() =>
                              setExpandedAlertId(
                                expandedAlertId === alert.id ? null : alert.id
                              )
                            }
                          >
                            {expandedAlertId === alert.id
                              ? t("button.hideDevices")
                              : t("button.viewDevices")}
                          </button>
                        </div>
                      </div>

                      {expandedAlertId === alert.id && (
                        <div className="alerts-device-row">
                          <div className="alerts-device-grid">
                            <div className="alerts-device-grid-header">{t("label.deviceNameCol")}</div>
                            <div className="alerts-device-grid-header">{t("label.deviceTypeCol")}</div>
                            <div className="alerts-device-grid-header">{t("label.timestampCol")}</div>
                            <div className="alerts-device-grid-header">{t("label.statusLabel")}</div>

                            {(alert.devices || []).length > 0 ? (
                              (alert.devices || []).map((device, dIndex) => (
                                <React.Fragment key={dIndex}>
                                  <div>{device?.device_name || device?.camera_name || "Unknown"}</div>
                                  <div>{device?.device_type || "N/A"}</div>
                                  <div>
                                    {device?.recording_start || device?.recorded_at
                                      ? new Date(
                                          device.recording_start || device.recorded_at
                                        ).toLocaleString()
                                      : "N/A"}
                                  </div>
                                  <div>{device?.status || "N/A"}</div>
                                </React.Fragment>
                              ))
                            ) : (
                              <div className="alerts-no-devices alerts-no-devices-grid" style={{ gridColumn: "1 / -1" }}>
                                {t("label.noDevicesAlert")}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Pagination Footer */}
          {!loading &&
            (activeTab === "all" ? filteredAlerts : activeAlerts).length > 0 && (
              <div className="alerts-pagination">
                <div className="alerts-pagination-left">
                  <span className="alerts-pagination-label">{t("label.rows")}</span>
                  <CustomSelect
                    value={rowsPerPage}
                    onChange={(val) => {
                      setRowsPerPage(Number(val));
                      setCurrentPage(1);
                    }}
                    options={ROWS_PER_PAGE_OPTIONS.map(o => ({ ...o, label: t(o.labelKey) }))}
                    className="alerts-rows-custom-select"
                  />
                  <span className="alerts-pagination-info">
                    {activeTab === "all"
                      ? `${Math.min(
                          (currentPage - 1) * rowsPerPage + 1,
                          filteredAlerts.length
                        )} - ${Math.min(
                          currentPage * rowsPerPage,
                          filteredAlerts.length
                        )} of ${filteredAlerts.length}`
                      : `1 - ${activeAlerts.length} of ${activeAlerts.length}`}
                  </span>
                </div>
                <div className="alerts-pagination-right">
                  <button
                    className="alerts-page-arrow"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    <svg width="9" height="14" viewBox="0 0 9 14" fill="none">
                      <path
                        d="M7.5 1L1.5 7L7.5 13"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    className="alerts-page-arrow"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                  >
                    <svg width="9" height="14" viewBox="0 0 9 14" fill="none">
                      <path
                        d="M1.5 1L7.5 7L1.5 13"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}
        </div>
      </main>
    </div>
  );
}

export default AlertsPage;
